import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  TwitterShareButton,
  TelegramIcon,
  TelegramShareButton,
  WhatsappIcon,
  WhatsappShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  XIcon,
} from "react-share";

import {
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Fade,
  Paper,
  Divider,
  Button,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Share as ShareIcon,
  MoreHoriz as MoreIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { GENERAL } from "../general/General";
import { useUser } from "../authMethods/UserContext";

export function Share({ currentUrl, title, description, image, onClose }) {
  const { t } = useTranslation();
  const { userData } = useUser();
  const [code, setCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isVerySmall = useMediaQuery("(max-height: 700px)");

  useEffect(() => {
    setCode(userData.inviteCode);
  }, [userData]);

  const handleCopy = async () => {
    if (code) {
      try {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = code;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareDescription,
          url: shareUrl,
        });
      } catch (err) {
        console.log("Error sharing:", err);
      }
    }
  };

  const defaultUrl = GENERAL.HOST;
  const defaultTitle = t("Share_Title");
  const defaultDescription = t("Share_Description");
  const defaultImage = GENERAL.SHARE_IMAGE;

  const shareUrl = currentUrl || defaultUrl;
  const shareTitle = title || defaultTitle;
  const shareDescription = description || defaultDescription;
  const shareImage = image || defaultImage;

  const iconSize = isVerySmall ? 28 : 32;

  // Primary social platforms (most used)
  const primaryPlatforms = [
    { 
      component: WhatsappShareButton, 
      icon: WhatsappIcon, 
      props: { url: shareUrl, title: shareTitle },
      color: "#25D366"
    },
    { 
      component: TelegramShareButton, 
      icon: TelegramIcon, 
      props: { url: shareUrl, title: shareTitle },
      color: "#0088cc"
    },
    { 
      component: TwitterShareButton, 
      icon: XIcon, 
      props: { url: shareUrl, title: shareTitle },
      color: "#000000"
    },
    { 
      component: FacebookShareButton, 
      icon: FacebookIcon, 
      props: { url: shareUrl },
      color: "#1877f2"
    },
    { 
      component: LinkedinShareButton, 
      icon: LinkedinIcon, 
      props: { url: shareUrl },
      color: "#0077b5"
    },
    { 
      component: EmailShareButton, 
      icon: EmailIcon, 
      props: { url: shareUrl, subject: shareTitle },
      color: "#7f7f7f"
    },
  ];

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        maxHeight: isVerySmall ? "600px" : "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Helmet>
        <meta property="og:title" content={shareTitle} />
        <meta property="og:description" content={shareDescription} />
        <meta property="og:image" content={shareImage} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="website" />
        <title>{shareTitle}</title>
      </Helmet>

      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 0,
          position: "relative",
        }}
      >
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{ 
              position: "absolute", 
              top: 8, 
              right: 8, 
              color: "white",
              backgroundColor: "rgba(255,255,255,0.1)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" }
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}

        <Box textAlign="center" mt={1}>
          <ShareIcon sx={{ fontSize: 28, mb: 1, opacity: 0.9 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: isVerySmall ? "1rem" : "1.1rem",
              letterSpacing: 0.5,
            }}
          >
            {t("Share & Invite Friends")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              opacity: 0.8,
              fontSize: isVerySmall ? "0.75rem" : "0.85rem",
              mt: 0.5,
            }}
          >
            {t("Choose your preferred platform")}
          </Typography>
        </Box>
      </Paper>

      {/* Main Content */}
      <Box sx={{ flex: 1, p: 2, overflow: "auto" }}>
        {/* Native Share (if available) */}
        {navigator.share && (
          <Box mb={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleNativeShare}
              sx={{
                py: 1.5,
                borderRadius: 3,
                background: "linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)",
                boxShadow: "0 3px 5px 2px rgba(255, 105, 135, .3)",
                textTransform: "none",
                fontSize: "0.95rem",
                fontWeight: 600,
              }}
              startIcon={<ShareIcon />}
            >
              {t("Share with Apps")}
            </Button>
          </Box>
        )}

        {/* Quick Share Platforms */}
        <Box mb={3}>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1.5,
              fontWeight: 600,
              color: "text.secondary",
              textAlign: "center",
              fontSize: isVerySmall ? "0.8rem" : "0.9rem",
            }}
          >
            {t("Quick Share")}
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            flexWrap="wrap"
            useFlexGap
          >
            {primaryPlatforms.map((platform, index) => {
              const ShareButton = platform.component;
              const ShareIcon = platform.icon;
              
              return (
                <Fade in timeout={300 + index * 100} key={index}>
                  <Tooltip title={platform.component.name?.replace('ShareButton', '') || 'Share'}>
                    <Box>
                      <ShareButton {...platform.props}>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: `0 4px 20px ${platform.color}40`,
                            },
                            "&:active": {
                              transform: "translateY(0px)",
                            },
                          }}
                        >
                          <ShareIcon size={iconSize} round />
                        </Paper>
                      </ShareButton>
                    </Box>
                  </Tooltip>
                </Fade>
              );
            })}
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }}>
          <Chip 
            label={t("OR")} 
            size="small" 
            sx={{ 
              backgroundColor: "primary.main", 
              color: "white",
              fontSize: "0.75rem"
            }} 
          />
        </Divider>

        {/* Invite Code Section */}
        <Box textAlign="center">
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              color: "text.secondary",
              fontSize: isVerySmall ? "0.8rem" : "0.9rem",
            }}
          >
            {t("Send your invite code directly")}
          </Typography>

          {code && (
            <Paper
              elevation={1}
              sx={{
                p: 2,
                backgroundColor: "grey.50",
                borderRadius: 3,
                border: "2px dashed",
                borderColor: "primary.main",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.75rem", mb: 1 }}
              >
                {t("Your Invite Code")}
              </Typography>
              
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "monospace",
                  backgroundColor: "white",
                  py: 1,
                  px: 2,
                  borderRadius: 2,
                  fontSize: isVerySmall ? "0.9rem" : "1rem",
                  fontWeight: 700,
                  letterSpacing: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  mb: 1.5,
                }}
              >
                {code}
              </Typography>

              <Button
                onClick={handleCopy}
                variant={copied ? "contained" : "outlined"}
                color={copied ? "success" : "primary"}
                size="small"
                fullWidth
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1,
                  transition: "all 0.3s ease",
                }}
                startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
              >
                {copied ? t("Copied!") : t("Copy Code")}
              </Button>
            </Paper>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mt: 1.5,
              fontSize: isVerySmall ? "0.8rem" : "0.8rem",
              fontStyle: "italic",
            }}
          >
            {t("Your friend should have an account already")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}